import type { IAPIRequestConfig, IHTTPResponse } from '@yimoka/shared';
import { isVacuous } from '@yimoka/shared';
import { createWebHttpClient, type IWebHttpClient } from '@yimoka/web-http';

import { ENV } from '@/env';
import {
  applyPortalArticleTenantHeader,
  PORTAL_ARTICLE_TENANT_HEADER,
  PORTAL_ARTICLE_TENANT_ICUA,
} from '@/lib/portal-article-tenant';
import { getApp, getClientID, getLanguage } from '@/root';

export { PORTAL_ARTICLE_TENANT_HEADER, PORTAL_ARTICLE_TENANT_ICUA };

let webHttpClient: IWebHttpClient | undefined;

function getWebHttpClient() {
  if (!webHttpClient) {
    webHttpClient = createWebHttpClient({
      baseURL: ENV.api,
      timeout: 30000,
      channel: 'web',
      useType: 'client',
      platform: typeof navigator === 'undefined' ? 'web' : navigator.platform,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      getClientID,
      getLanguage,
      getTenantID: () => ENV.tenantID,
      getApp,
    });
  }

  return webHttpClient;
}

const httpTarget: IWebHttpClient['instance'] = Object.create(null);

export const http = new Proxy(httpTarget, {
  get: (_target, property, receiver) => Reflect.get(getWebHttpClient().instance, property, receiver),
});

const isIcuaApiUrl = (url?: string) => !!url && (url.includes('/edu/') || url.includes('/service/frontend/'));

function normalizeConfig<P>(config: IAPIRequestConfig<P>): IAPIRequestConfig<P> {
  const next: IAPIRequestConfig<P> = { ...config };
  const headers: Record<string, string> = { ...(next.headers ?? {}) };
  const { url } = next;

  applyPortalArticleTenantHeader(headers, ENV.icuaTenantID);

  // ICUA CIPP API（课程等）直连 api.icua-cipp.cn，不经过本地代理。
  if (isIcuaApiUrl(url)) {
    next.baseURL = '';
    next.url = `https://api.icua-cipp.cn/api${url}`;
    if (ENV.courseTenantID && isVacuous(headers['x-md-global-tenantID'])) {
      headers['x-md-global-tenantID'] = ENV.courseTenantID;
    }
  }

  next.headers = headers;
  if (String(next.method).toLowerCase() === 'post' && typeof next.data === 'undefined') {
    // 旧接口 post 空 body 时需要对象，避免 content-type 被清空。
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    next.data = {} as P;
  }
  return next;
}

export const apiExecutor: IWebHttpClient['apiExecutor'] = config => getWebHttpClient().apiExecutor(normalizeConfig(config));

/**
 * ICUA 租户门户文章 API 执行器（列表 / 详情 / 分类）
 */
export const icuaArticleHttpRequest: IHTTPRequest = config => httpRequest({
  ...config,
  headers: {
    ...(config.headers ?? {}),
    [PORTAL_ARTICLE_TENANT_HEADER]: PORTAL_ARTICLE_TENANT_ICUA,
  },
});

export const httpRequest: IHTTPRequest = <R = unknown, P = unknown>(config: IAPIRequestConfig<P>) => (
  apiExecutor(config) as Promise<IHTTPResponse<R, P>>
);

export const httpGet: IHTTPGet = (url, config) => httpRequest({ ...config, url, method: 'GET' });
export const httpDelete: IHTTPGet = (url, config) => httpRequest({ ...config, url, method: 'DELETE' });
export const httpHead: IHTTPGet = (url, config) => httpRequest({ ...config, url, method: 'HEAD' });
export const httpOptions: IHTTPGet = (url, config) => httpRequest({ ...config, url, method: 'OPTIONS' });

export const httpPost: IHTTPPost = (url, data, config) => httpRequest({ ...config, url, data, method: 'POST' });
export const httpPut: IHTTPPost = (url, data, config) => httpRequest({ ...config, url, data, method: 'PUT' });
export const httpPatch: IHTTPPost = (url, data, config) => httpRequest({ ...config, url, data, method: 'PATCH' });

export type IHTTPRequest = <R = unknown, P = unknown>(config: IAPIRequestConfig<P>) => Promise<IHTTPResponse<R, P>>;

export type IHTTPGet = <R = unknown, P = unknown>(url: string, config?: Omit<IAPIRequestConfig<P>, 'url' | 'method'>) => Promise<IHTTPResponse<R, P>>;

export type IHTTPPost = <R = unknown, P = Record<string | number | symbol, unknown>>(url: string, data?: P, config?: Omit<IAPIRequestConfig<P>, 'url' | 'method' | 'data'>) => Promise<IHTTPResponse<R, P>>;
